import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import TrueImpactSimpleModal from 'shared/design-system/components/TrueImpactSimpleModal';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactTextIndicator from 'shared/design-system/components/TrueImpactTextIndicator';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactAlertIcon from 'shared/design-system/components/TrueImpactAlertIcon';

const MAX_NOTE_LENGTH = 500;

const StyledDiv = styled.div`
  padding: ${SPACERS.large} ${SPACERS.medium} ${SPACERS.medium};
  .icon-row {
    display: flex;
    justify-content: center;
    margin-bottom: ${SPACERS.medium};
  }
  .message {
    margin: 0;
    line-height: 1.7;
  }
  .note-wrapper {
    margin-top: ${SPACERS.medium};
  }
`;

export const matchesOrgName = (
  programName: string,
  orgName: string | undefined,
): boolean => {
  if (!orgName) return false;
  const p = programName.toLowerCase().trim();
  const o = orgName.toLowerCase().trim();
  return p === o || p.includes(o) || o.includes(p);
};

interface OrgNameWarningModalProps {
  isMatchedDonorName: boolean;
  open: boolean;
  onClose: () => void;
  onConfirm: (level1Note: string) => void;
  confirmText?: string;
  programName: string | null | undefined;
}

const OrgNameWarningModal: React.FC<OrgNameWarningModalProps> = ({
  open,
  onClose,
  onConfirm,
  programName,
  isMatchedDonorName,
  confirmText = 'Save',
}) => {
  const [level1Note, setLevel1Note] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleNoteMouseDownCapture = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <TrueImpactSimpleModal
      open={open}
      onClose={onClose}
      contentStyle={{
        maxWidth: 480,
        borderRadius: 4,
        overflow: 'hidden',
      }}
      next={{
        text: isMatchedDonorName
          ? confirmText
          : `${confirmText} "${programName}"`,
        onClick: () => onConfirm(level1Note),
        disabled:
          level1Note.trim() === '' || level1Note.length > MAX_NOTE_LENGTH,
      }}
      previous={{
        text: 'Cancel',
        onClick: onClose,
      }}
    >
      <StyledDiv>
        <div className="icon-row">
          <TrueImpactAlertIcon
            icon={faTriangleExclamation}
            color="warningDark"
            shadowOpacity={0.15}
          />
        </div>
        {isMatchedDonorName ? (
          <p className="message">
            A donor name has been detected in your response. Please remove all
            references to individual donor names unless the donor has formal
            naming rights to the program. If you need to retain the donor name,
            your report will default to private and not be available to share in
            the Impact Reporting Network.
          </p>
        ) : (
          <p className="message">
            Your report title matches your organization name. True Impact
            reports are built around a single program, not your whole
            organization. Try naming this report after a specific program
            you&#39;re reporting on.
          </p>
        )}
        <div
          className="note-wrapper"
          onMouseDownCapture={handleNoteMouseDownCapture}
        >
          <TrueImpactTextfield
            isTextarea
            inputRef={textareaRef}
            placeholder={
              isMatchedDonorName
                ? 'Please provide a brief explanation why the report should include the funder\'s name.'
                : 'If this is the name of your program, please provide a brief explanation of what this report is about.'
            }
            value={level1Note}
            onChange={setLevel1Note}
            maxLength={MAX_NOTE_LENGTH}
          />
          <TrueImpactTextIndicator
            minLength={0}
            text={level1Note}
            maxLength={MAX_NOTE_LENGTH}
          />
        </div>
      </StyledDiv>
    </TrueImpactSimpleModal>
  );
};

export default OrgNameWarningModal;
